import { useEffect, useMemo, useReducer, useRef } from 'react';

import { type AntibodyNodeGroup, type AntibodyResponse, type ClonotypeDefinitionEnum, generatedOpenAPI } from '../../generated/generatedOpenAPI';
import type { SelectedNode } from '../../store/slices/clonotypes.slice';

export interface UseAntibodyTableDataParams {
  experimentId: string;
  barcodes: string[];
  nodes: SelectedNode[];
  clonotypeDefinition: ClonotypeDefinitionEnum;
}

export interface UseAntibodyTableDataResult {
  clonotypes: AntibodyResponse[];
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
}

// Max cached responses. Caps memory (each entry is a full AntibodyResponse[]); 10 is plenty to
// keep re-selecting a clonotype instant.
const CACHE_LIMIT = 10;

// The Map preserves insertion order, so it doubles as an LRU: touch = delete + re-insert (moves the
// key to the newest position); eviction removes from the front (oldest).
function cacheTouch(cache: Map<string, AntibodyResponse[]>, key: string) {
  const value = cache.get(key);
  if (value === undefined) {
    return;
  }
  cache.delete(key);
  cache.set(key, value);
}

function cacheWrite(cache: Map<string, AntibodyResponse[]>, key: string, value: AntibodyResponse[]) {
  cache.delete(key);
  cache.set(key, value);
  while (cache.size > CACHE_LIMIT) {
    const oldest = cache.keys().next().value as string | undefined;
    if (oldest === undefined) {
      break;
    }
    cache.delete(oldest);
  }
}

/**
 * Hook to fetch antibody table data for the Antibody Analysis dashboard.
 *
 * Wraps the RTK Query mutation to provide a clean query-like interface with automatic fetching when
 * the selection changes.
 *
 * Source-of-truth guarantee: the returned `clonotypes` ALWAYS correspond to the current selection
 * (`barcodes`).
 */
export function useAntibodyTableData({ experimentId, barcodes, nodes, clonotypeDefinition }: UseAntibodyTableDataParams): UseAntibodyTableDataResult {
  const [fetchData, { isLoading, error }] = generatedOpenAPI.useFetchAntibodyTableDataApiStellarExperimentsExperimentIdAntibodyTablePostMutation();

  // The key of the fetch we've kicked off (or served from cache) most recently — so we don't refire
  // the same request on every render. Reset to null on failure so the selection can be retried.
  const lastFetchedRef = useRef<string | null>(null);
  const cacheRef = useRef<Map<string, AntibodyResponse[]>>(new Map());
  // Async cache writes live in a ref (no re-render); bump to re-render once a response lands.
  const [, bumpCache] = useReducer((n: number) => n + 1, 0);

  // Deduplication key — prevents re-fetching on re-renders when selection hasn't changed.
  const cacheKey = useMemo(
    () => (barcodes.length > 0 ? `${experimentId}-${clonotypeDefinition}-${[...barcodes].sort().join(',')}` : null),
    [barcodes, experimentId, clonotypeDefinition],
  );

  useEffect(() => {
    if (!cacheKey) {
      return;
    }
    if (cacheRef.current.has(cacheKey)) {
      // Cache hit — mark most-recently-used and serve it below; no fetch (instant re-select).
      cacheTouch(cacheRef.current, cacheKey);
      lastFetchedRef.current = cacheKey;
      return;
    }
    if (cacheKey === lastFetchedRef.current) {
      // Already fetching this exact selection — don't refire.
      return;
    }
    lastFetchedRef.current = cacheKey;
    const keyForThisFetch = cacheKey;
    // Map SelectedNode → AntibodyNodeGroup inside the effect so nodes is a stable dep via cacheKey.
    const apiNodes: AntibodyNodeGroup[] = nodes.map((n) => ({ clonotypeId: n.clonotypeId, nodeId: n.nodeId, cellIds: n.cellIds }));
    fetchData({ experimentId, antibodyTableRequest: { clonotypeDefinition, barcodes, nodes: apiNodes, aps: true } })
      .unwrap()
      .then((response) => {
        // Cache under the key of THIS request — never the (possibly newer) current key — so results
        // can't be attributed to the wrong selection.
        if (response?.clonotypes) {
          cacheWrite(cacheRef.current, keyForThisFetch, response.clonotypes);
          bumpCache();
        }
      })
      .catch(() => {
        // Failed fetch: unpin so re-selecting this set retries instead of staying stuck. We surface
        // the error (below) and show no rows rather than a stale selection's data.
        if (lastFetchedRef.current === keyForThisFetch) {
          lastFetchedRef.current = null;
        }
      });
  }, [cacheKey, experimentId, clonotypeDefinition, barcodes, nodes, fetchData]);

  // Manual refresh for the current selection — drops its cache entry and refetches.
  const refetch = () => {
    if (!cacheKey) {
      return;
    }
    cacheRef.current.delete(cacheKey);
    lastFetchedRef.current = null;
    const apiNodes: AntibodyNodeGroup[] = nodes.map((n) => ({ clonotypeId: n.clonotypeId, nodeId: n.nodeId, cellIds: n.cellIds }));
    fetchData({ experimentId, antibodyTableRequest: { clonotypeDefinition, barcodes, nodes: apiNodes, aps: true } })
      .unwrap()
      .then((response) => {
        if (response?.clonotypes) {
          cacheWrite(cacheRef.current, cacheKey, response.clonotypes);
          bumpCache();
        }
      })
      .catch(() => {});
  };

  const hasSelection = barcodes.length > 0;
  const currentClonotypes = cacheKey ? cacheRef.current.get(cacheKey) : undefined;

  return {
    clonotypes: hasSelection && currentClonotypes ? currentClonotypes : [],
    isLoading: hasSelection && !currentClonotypes,
    // Surface the error only when we have no data to show for the current selection.
    error: hasSelection && !currentClonotypes ? error : undefined,
    refetch,
  };
}
